import { FormState } from 'react-hook-form'
import { Emc, GeneralSettings, HeatpumpIo, Htc320hIo, InstallationFormModel, Storage, StorageIo } from '../../forms'
import { checkTrueRecursively } from 'src/Libs/flow/utils'
import { getTreatHtcAsNonSgReady } from '../../utils'

export const getIsInstallationDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  const { dirtyFields } = installationState
  return Boolean(
    dirtyFields.name ||
      dirtyFields.generalSettings?.location?.timeZone ||
      dirtyFields.generalSettings?.location?.country ||
      dirtyFields.generalSettings?.location?.street ||
      dirtyFields.generalSettings?.location?.houseNumber ||
      dirtyFields.generalSettings?.location?.location ||
      dirtyFields.generalSettings?.location?.postalCode
  )
}

export const getIsInstallationConfigDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  const { dirtyFields } = installationState

  return Boolean(
    dirtyFields.storageSystem?.generalSettings?.wiringTopologyDeviceId ||
      dirtyFields.storageSystem?.generalSettings?.systemRegulation?.peakPower ||
      dirtyFields.storageSystem?.generalSettings?.systemRegulation?.hasFeedInLimit ||
      dirtyFields.storageSystem?.generalSettings?.systemRegulation?.feedInRatio ||
      dirtyFields.storageSystem?.generalSettings?.systemRegulation?.feedInMaxPower ||
      dirtyFields.storageSystem?.generalSettings?.chargingConfiguration?.priorityChargingDevice ||
      dirtyFields.storageSystem?.generalSettings?.chargingConfiguration?.useBatteryDischargeOnlyWhenPvIsExceeded ||
      dirtyFields.storageSystem?.generalSettings?.chargingConfiguration?.useBatteryDischargeOnlyInOtherMode ||
      dirtyFields.storageSystem?.generalSettings?.advancedSettings?.weatherForecastOptimization?.useWeatherForecastBasedCharging ||
      dirtyFields.storageSystem?.generalSettings?.advancedSettings?.weatherForecastOptimization?.weatherOptimizationOption ||
      dirtyFields.storageSystem?.generalSettings?.advancedSettings?.emergencyPower?.useEmergencyPower ||
      dirtyFields.storageSystem?.generalSettings?.advancedSettings?.emergencyPower?.emergencyPowerDelay ||
      dirtyFields.generalSettings?.gridSettings?.numberOfPhases ||
      dirtyFields.generalSettings?.gridSettings?.mainProtection ||
      dirtyFields.generalSettings?.gridSettings?.thermalDerating
  )
}

export const getShouldUpdateEmcPairingId = (emcDevice: Emc | null): boolean => {
  if (!emcDevice) return false
  return emcDevice.pairingId !== emcDevice.initialPairingId
}

export const getIsEmcConfigDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  const { dirtyFields } = installationState
  return Boolean(
    dirtyFields.generalSettings?.gridSettings?.gridMeter?.electricalCurrentTransformerRatio ||
      dirtyFields.generalSettings?.gridSettings?.gridMeter?.hasElectricalCurrentTransformer
  )
}

export const getShouldUpdateStoragePairingId = (storageDevice?: Storage): boolean => {
  if (!storageDevice) return false
  return storageDevice.pairingId !== storageDevice.initialPairingId
}

export const getIsStorageConfigDirty = (installationState: FormState<InstallationFormModel>, storageIndex: number): boolean => {
  const { dirtyFields } = installationState
  const storageDevicesDirtyFields = dirtyFields.storageDevices
  const storageSpecificSettingsDirtyFields = dirtyFields.storageSystem?.storageSpecificSettings?.[storageIndex]

  return Boolean(
    storageSpecificSettingsDirtyFields?.chargingConfiguration?.trackersBehaviour ||
      storageSpecificSettingsDirtyFields?.advancedSettings?.batteryOperationRange?.isAuto ||
      storageSpecificSettingsDirtyFields?.advancedSettings?.batteryOperationRange?.maximumChargingPower ||
      storageSpecificSettingsDirtyFields?.advancedSettings?.batteryOperationRange?.maximumDischargingPower ||
      storageSpecificSettingsDirtyFields?.advancedSettings?.batteryOperationRange?.minimumConsumptionPowerToStartDischarge ||
      storageSpecificSettingsDirtyFields?.advancedSettings?.emergencyPower?.emergencyPowerReserve ||
      storageSpecificSettingsDirtyFields?.advancedSettings?.useStorageSystemEnergySavingMode ||
      (typeof storageDevicesDirtyFields === 'object' && Boolean(storageDevicesDirtyFields[storageIndex]?.name))
  )
}

export const getAreBlockingWeeklySchedulesDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  return (
    checkTrueRecursively(installationState.dirtyFields.storageSystem?.generalSettings?.advancedSettings?.chargeBlockingWeeklySchedule) ||
    checkTrueRecursively(installationState.dirtyFields.storageSystem?.generalSettings?.advancedSettings?.dischargeBlockingWeeklySchedule)
  )
}

export const getIsModbusTcpDirty = (installationState: FormState<InstallationFormModel>, modbusTcpIndex: number): boolean => {
  return checkTrueRecursively(installationState.dirtyFields.chargingStations?.modbusTcp?.[modbusTcpIndex])
}

export const getIsEebusDirty = (installationState: FormState<InstallationFormModel>, eebusIndex: number): boolean => {
  return checkTrueRecursively(installationState.dirtyFields.chargingStations?.eebus?.[eebusIndex])
}

export const getIsMeterDirty = (installationState: FormState<InstallationFormModel>, meterIndex: number): boolean => {
  return checkTrueRecursively(installationState.dirtyFields.meters?.[meterIndex])
}

export const getIsHtc320hIoDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  return checkTrueRecursively(installationState.dirtyFields.ioInterfaces?.htc320h)
}

export const getIsStorageIoDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  return checkTrueRecursively(installationState.dirtyFields.ioInterfaces?.storage)
}

export const getIsMyPvIoDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  return checkTrueRecursively(installationState.dirtyFields.ioInterfaces?.mypv)
}

export const getIsHeatpumpIoDirty = (installationState: FormState<InstallationFormModel>): boolean => {
  return checkTrueRecursively(installationState.dirtyFields.heatpump)
}

/** Get if HTC320H I/O should send a PUT request because it is possibly no longer treated as an SR-Ready device. */
export const getShouldUpdateHtc320hIoNominalPower = (
  htc320hIo: Htc320hIo | null,
  generalSettings: GeneralSettings,
  storage: StorageIo | null,
  heatpump: HeatpumpIo | null
): boolean => {
  if (!htc320hIo) return false

  const treatHtcAsNonSgReady = getTreatHtcAsNonSgReady(generalSettings, storage, heatpump)
  const nominalPower = htc320hIo.nominalPower

  return treatHtcAsNonSgReady && nominalPower !== 0
}
